import React, { useEffect, useState } from 'react';
import { api, EstimateBuilderDocDto, EstimateLineItemDto, EstimateLineType, ProspectDto, ServiceCatalogItemDto, InventoryItemDto, ContractorListItem } from '../../services/api';
import { Card, Btn, Badge, Input, Select, C } from '../../components/UI';
import { AddressAutocomplete } from '../../components/AddressAutocomplete';

// ── Estimate Builder — prospect-first, typed line items, branded PDF ─────────────────
// A new, standalone admin panel — not a retrofit of the older job-attached JobPricingBuilder/
// JobDetailEstimateSection pair, since this backend is a deliberately separate system (see
// Models.cs's EstimateBuilderDoc comment). List + detail views in one file, matching the
// shape of ServiceAreaPanel/InventoryPanel elsewhere in this dashboard.

const needsConversion = (e: EstimateBuilderDocDto) => e.status === 'Approved' && !e.convertedJobId;
const attentionRank = (e: EstimateBuilderDocDto) => e.status === 'Requested' ? 0 : needsConversion(e) ? 1 : 2;

export function EstimateBuilderPanel({ initialSelectedId, initialNewProspect, onConsumeNewProspect }: {
    initialSelectedId?: string | null;
    // Set when an admin routes here from "Book for Customer" with "This is an estimate instead"
    // — opens straight into the new-estimate form, prefilled, instead of making them re-enter a
    // customer they already just identified.
    initialNewProspect?: { name: string; email?: string; phone?: string } | null;
    onConsumeNewProspect?: () => void;
}) {
    const [estimates, setEstimates] = useState<EstimateBuilderDocDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedId, setSelectedId] = useState<string | null>(initialSelectedId ?? null);
    const [showNew, setShowNew] = useState(!!initialNewProspect);

    const load = () => {
        setLoading(true); setError('');
        api.getEstimateBuilderDocs().then(setEstimates).catch(e => setError(e.message)).finally(() => setLoading(false));
    };
    useEffect(load, []);

    if (selectedId) {
        return <EstimateDetail id={selectedId} onBack={() => { setSelectedId(null); load(); }} />;
    }

    return (
        <div style={{ maxWidth: 900 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Estimates</h2>
                <Btn size="sm" onClick={() => setShowNew(true)}>+ New Estimate</Btn>
            </div>
            {error && <div style={{ color: '#DC2626', fontSize: 13, marginBottom: 12 }}>{error}</div>}

            {showNew && (
                <NewEstimateForm
                    initialProspect={initialNewProspect ?? null}
                    onCreated={(id) => { setShowNew(false); onConsumeNewProspect?.(); setSelectedId(id); }}
                    onCancel={() => { setShowNew(false); onConsumeNewProspect?.(); }}
                />
            )}

            {loading ? (
                <div style={{ fontSize: 13, color: C.textSub }}>Loading…</div>
            ) : estimates.length === 0 ? (
                <div style={{ color: C.textMuted, fontSize: 13 }}>No estimates yet — create your first one above.</div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {/* Customer/prospect-submitted requests and approved-but-unconverted estimates
                        both need a look first — an admin either builds/dispatches a fresh request,
                        or converts an approved one to a job — so surface both ahead of everything
                        else instead of mixed in by date. */}
                    {[...estimates].sort((a, b) => attentionRank(a) - attentionRank(b)).map(e => (
                        <Card key={e.id} style={{ cursor: 'pointer', ...(e.status === 'Requested' ? { borderLeft: '4px solid #F59E0B' } : needsConversion(e) ? { borderLeft: '4px solid #2563EB' } : {}) }}>
                            <div onClick={() => setSelectedId(e.id)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <strong>EB-{e.number}</strong>
                                        <Badge label={e.status === 'Requested' ? 'New request' : needsConversion(e) ? 'Ready to convert' : e.status} />
                                        {e.revisionNumber > 1 && <span style={{ fontSize: 11, color: C.textMuted }}>rev {e.revisionNumber}</span>}
                                        {e.assignedContractorName && <span style={{ fontSize: 11, color: C.textMuted }}>→ assigned to {e.assignedContractorName}</span>}
                                    </div>
                                    <div style={{ fontSize: 13, color: C.textSub, marginTop: 4 }}>{e.prospect.name} · {e.jobSiteAddress}</div>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                    <div style={{ fontWeight: 700, fontSize: 15 }}>${e.totalAmount.toFixed(2)}</div>
                                    <div style={{ fontSize: 11, color: C.textMuted }}>{new Date(e.createdAt).toLocaleDateString()}</div>
                                </div>
                            </div>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}

interface AddressGeo { lat?: number; lng?: number; city?: string; state?: string; county?: string; postalCode?: string; country?: string }

function NewEstimateForm({ onCreated, onCancel, initialProspect }: {
    onCreated: (id: string) => void; onCancel: () => void;
    initialProspect?: { name: string; email?: string; phone?: string } | null;
}) {
    const [selectedProspect, setSelectedProspect] = useState<ProspectDto | null>(null);
    // Already know who this is (routed here from "Book for Customer") — skip straight past the
    // prospect search step instead of making the admin look them up again.
    const [showNewProspect, setShowNewProspect] = useState(!!initialProspect);
    const [prospectSearch, setProspectSearch] = useState('');
    const [prospectResults, setProspectResults] = useState<ProspectDto[]>([]);
    const [searching, setSearching] = useState(false);

    const [name, setName] = useState(initialProspect?.name ?? '');
    const [email, setEmail] = useState(initialProspect?.email ?? '');
    const [phone, setPhone] = useState(initialProspect?.phone ?? '');
    const [billingAddress, setBillingAddress] = useState('');
    const [billingGeo, setBillingGeo] = useState<AddressGeo>({});

    const [jobSiteAddress, setJobSiteAddress] = useState('');
    const [jobSiteGeo, setJobSiteGeo] = useState<AddressGeo>({});

    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    // Show a handful of recent prospects as soon as the form opens, so there's something to
    // pick from before the admin types anything.
    useEffect(() => { api.searchProspects('').then(setProspectResults).catch(() => {}); }, []);

    const runProspectSearch = async () => {
        setSearching(true); setError('');
        try { setProspectResults(await api.searchProspects(prospectSearch)); }
        catch (e: any) { setError(e.message); }
        finally { setSearching(false); }
    };

    const create = async () => {
        if (!selectedProspect && !name.trim()) { setError('Prospect name is required.'); return; }
        if (!jobSiteAddress.trim()) { setError('Job site address is required.'); return; }
        setSaving(true); setError('');
        try {
            const est = await api.createEstimateBuilderDoc({
                prospectId: selectedProspect?.id,
                newProspect: selectedProspect ? undefined : {
                    name: name.trim(), email: email.trim() || null, phone: phone.trim() || null,
                    billingAddress: billingAddress.trim() || null,
                    billingLat: billingGeo.lat ?? null, billingLng: billingGeo.lng ?? null,
                    billingCity: billingGeo.city ?? null, billingState: billingGeo.state ?? null,
                    billingPostalCode: billingGeo.postalCode ?? null, billingCountry: billingGeo.country ?? null,
                },
                jobSiteAddress: jobSiteAddress.trim(),
                jobSiteLat: jobSiteGeo.lat ?? null, jobSiteLng: jobSiteGeo.lng ?? null,
                jobSiteCity: jobSiteGeo.city ?? null, jobSiteState: jobSiteGeo.state ?? null, jobSiteCounty: jobSiteGeo.county ?? null,
                jobSitePostalCode: jobSiteGeo.postalCode ?? null, jobSiteCountry: jobSiteGeo.country ?? null,
            });
            onCreated(est.id);
        } catch (e: any) { setError(e.message); }
        finally { setSaving(false); }
    };

    return (
        <Card style={{ marginBottom: 20, borderLeft: `4px solid ${C.greenMid}` }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 15 }}>New estimate</h3>
            {error && <div style={{ color: '#DC2626', fontSize: 13, marginBottom: 12 }}>{error}</div>}

            {selectedProspect ? (
                <div style={{ background: C.bg, borderRadius: 8, padding: 12, marginBottom: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <div style={{ fontWeight: 700, fontSize: 13 }}>{selectedProspect.name}</div>
                        <div style={{ fontSize: 12, color: C.textSub }}>{selectedProspect.email || selectedProspect.phone || 'No contact info on file'}</div>
                    </div>
                    <button onClick={() => setSelectedProspect(null)} style={{ background: 'none', border: 'none', color: C.textSub, cursor: 'pointer', fontSize: 12 }}>Change</button>
                </div>
            ) : !showNewProspect ? (
                <div style={{ marginBottom: 14 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                        <div style={{ flex: 1 }}>
                            <Input label="Search prospects" value={prospectSearch} onChange={setProspectSearch} placeholder="Name, email, or phone" />
                        </div>
                        <Btn size="sm" onClick={runProspectSearch} disabled={searching} style={{ marginBottom: 14 }}>{searching ? '...' : 'Search'}</Btn>
                    </div>
                    <div style={{ maxHeight: 160, overflowY: 'auto', marginBottom: 10 }}>
                        {prospectResults.map(p => (
                            <div key={p.id} onClick={() => setSelectedProspect(p)} style={{ padding: '8px 10px', borderRadius: 6, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', border: `1px solid ${C.borderLight}`, marginBottom: 6 }}>
                                <span style={{ fontSize: 13, fontWeight: 600 }}>{p.name}</span>
                                <span style={{ fontSize: 12, color: C.textSub }}>{p.email || p.phone}</span>
                            </div>
                        ))}
                        {prospectResults.length === 0 && !searching && <div style={{ fontSize: 12, color: C.textMuted, padding: '8px 0' }}>No matching prospects — search above or create a new one.</div>}
                    </div>
                    <Btn variant="outline" fullWidth onClick={() => setShowNewProspect(true)}>+ New Prospect</Btn>
                </div>
            ) : (
                <div style={{ marginBottom: 14 }}>
                    <div style={{ display: 'flex', gap: 12 }}>
                        <div style={{ flex: 1 }}><Input label="Prospect name" value={name} onChange={setName} placeholder="Jane Smith" /></div>
                        <div style={{ flex: 1 }}><Input label="Email (optional)" value={email} onChange={setEmail} placeholder="jane@example.com" /></div>
                    </div>
                    <Input label="Phone (optional)" value={phone} onChange={setPhone} placeholder="(555) 555-1234" />
                    <AddressAutocomplete
                        label="Billing address (optional)"
                        value={billingAddress}
                        onChange={r => { setBillingAddress(r.address); setBillingGeo({ lat: r.lat, lng: r.lng, city: r.city, state: r.state, postalCode: r.postalCode, country: r.country }); }}
                        placeholder="123 Main St, City, ST"
                    />
                    <Btn variant="outline" onClick={() => setShowNewProspect(false)}>← Back to search</Btn>
                </div>
            )}

            <AddressAutocomplete
                label="Job site address"
                value={jobSiteAddress}
                onChange={r => { setJobSiteAddress(r.address); setJobSiteGeo({ lat: r.lat, lng: r.lng, city: r.city, state: r.state, county: r.county, postalCode: r.postalCode, country: r.country }); }}
                placeholder="123 Main St, Huntsville, AL"
                required
            />
            <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                <Btn variant="outline" onClick={onCancel}>Cancel</Btn>
                <Btn onClick={create} disabled={saving} fullWidth>{saving ? 'Creating…' : 'Create draft'}</Btn>
            </div>
        </Card>
    );
}

const LINE_TYPE_OPTIONS = [
    { value: 'Service', label: 'Service' },
    { value: 'Labor', label: 'Labor' },
    { value: 'Material', label: 'Material' },
];

function EstimateDetail({ id, onBack }: { id: string; onBack: () => void }) {
    const [estimate, setEstimate] = useState<EstimateBuilderDocDto | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [sentLink, setSentLink] = useState<string | null>(null);
    const [contractors, setContractors] = useState<ContractorListItem[]>([]);
    const [showAssign, setShowAssign] = useState(false);
    const [assignContractorId, setAssignContractorId] = useState('');

    const load = () => {
        setLoading(true); setError('');
        api.getEstimateBuilderDoc(id).then(setEstimate).catch(e => setError(e.message)).finally(() => setLoading(false));
    };
    useEffect(load, [id]);
    useEffect(() => { api.getContractors().then(setContractors).catch(() => {}); }, []);

    const assign = async () => {
        if (!assignContractorId) { setError('Pick a Runner to assign this to.'); return; }
        setBusy(true); setError('');
        try {
            setEstimate(await api.assignEstimateToContractor(id, assignContractorId));
            setShowAssign(false);
        } catch (e: any) { setError(e.message); } finally { setBusy(false); }
    };

    const locked = estimate ? (estimate.status === 'Approved' || estimate.status === 'Declined') : false;

    const send = async () => {
        setBusy(true); setError('');
        try {
            const res = await api.sendEstimateBuilderDoc(id);
            setEstimate(res.estimate);
            setSentLink(res.publicLink);
        } catch (e: any) { setError(e.message); } finally { setBusy(false); }
    };
    const markApproved = async () => {
        setBusy(true); setError('');
        try { setEstimate(await api.approveEstimateBuilderDoc(id)); } catch (e: any) { setError(e.message); } finally { setBusy(false); }
    };
    const decline = async () => {
        setBusy(true); setError('');
        try { setEstimate(await api.declineEstimateBuilderDoc(id)); } catch (e: any) { setError(e.message); } finally { setBusy(false); }
    };
    const viewPdf = async () => {
        setBusy(true); setError('');
        try {
            const { pdfUrl } = await api.getEstimateBuilderPdf(id);
            window.open(pdfUrl, '_blank');
        } catch (e: any) { setError(e.message); } finally { setBusy(false); }
    };
    const convertToJob = async () => {
        setBusy(true); setError('');
        try { setEstimate(await api.convertEstimateBuilderDocToJob(id)); } catch (e: any) { setError(e.message); } finally { setBusy(false); }
    };
    const createCustomer = async () => {
        setBusy(true); setError('');
        try { setEstimate(await api.createEstimateCustomer(id)); } catch (e: any) { setError(e.message); } finally { setBusy(false); }
    };

    if (loading) return <div style={{ fontSize: 13, color: C.textSub }}>Loading…</div>;
    if (!estimate) return <div style={{ color: '#DC2626' }}>{error || 'Estimate not found.'}</div>;

    return (
        <div style={{ maxWidth: 900 }}>
            <button onClick={onBack} style={{ background: 'transparent', border: 'none', color: C.greenMid, cursor: 'pointer', fontSize: 13, marginBottom: 12, padding: 0 }}>
                ← Back to estimates
            </button>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>EB-{estimate.number}</h2>
                        <Badge label={estimate.status} />
                    </div>
                    <div style={{ fontSize: 13, color: C.textSub, marginTop: 4 }}>
                        {estimate.prospect.name}{estimate.prospect.email ? ` · ${estimate.prospect.email}` : ''}{estimate.prospect.phone ? ` · ${estimate.prospect.phone}` : ''}
                        {estimate.prospect.customerId && <span style={{ marginLeft: 8, color: C.green, fontWeight: 600 }}>· Customer account linked</span>}
                    </div>
                    <div style={{ fontSize: 13, color: C.textSub }}>Job site: {estimate.jobSiteAddress}</div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <Btn size="sm" variant="outline" onClick={viewPdf} disabled={busy}>📄 PDF</Btn>
                    {!locked && !estimate.convertedJobId && (
                        <Btn size="sm" variant="outline" onClick={() => setShowAssign(s => !s)} disabled={busy}>
                            {estimate.assignedContractorId ? 'Reassign' : 'Assign to a Runner'}
                        </Btn>
                    )}
                    {/* Matches the backend's SendAsync allowance exactly — Requested is the status a
                        customer-initiated estimate sits in while an admin/Runner builds out its line
                        items, and never auto-transitions to Draft, so it has to be included here too. */}
                    {(estimate.status === 'Draft' || estimate.status === 'ChangesRequested' || estimate.status === 'Requested') && (
                        <Btn size="sm" onClick={send} disabled={busy || estimate.lineItems.length === 0}>Send</Btn>
                    )}
                    {(estimate.status === 'Sent' || estimate.status === 'Viewed') && (
                        <>
                            <Btn size="sm" variant="outline" onClick={decline} disabled={busy}>Decline</Btn>
                            <Btn size="sm" onClick={markApproved} disabled={busy}>Mark approved</Btn>
                        </>
                    )}
                    {estimate.status === 'Approved' && !estimate.prospect.customerId && (
                        <Btn size="sm" variant="outline" onClick={createCustomer} disabled={busy}>Create customer account</Btn>
                    )}
                    {estimate.status === 'Approved' && !estimate.convertedJobId && (
                        <Btn size="sm" onClick={convertToJob} disabled={busy}>Convert to job</Btn>
                    )}
                </div>
            </div>

            {showAssign && (
                <Card style={{ marginBottom: 16, borderLeft: '4px solid #F59E0B' }}>
                    <h3 style={{ margin: '0 0 10px', fontSize: 14 }}>Assign to a Runner</h3>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
                        <div style={{ flex: 1 }}>
                            <Select label="Runner" value={assignContractorId} onChange={setAssignContractorId}
                                options={[{ value: '', label: '— Pick a Runner —' }, ...contractors.map(c => ({ value: c.id, label: c.name }))]} />
                        </div>
                        <Btn size="sm" onClick={assign} disabled={busy} style={{ marginBottom: 14 }}>Assign</Btn>
                    </div>
                    <div style={{ fontSize: 11, color: C.textMuted }}>They'll be notified and can build/complete this estimate on-site with the customer.</div>
                </Card>
            )}

            {(estimate.requestNotes || estimate.requestPhotoUrls.length > 0) && (
                <Card style={{ marginBottom: 16, borderLeft: '4px solid #F59E0B' }}>
                    <h3 style={{ margin: '0 0 10px', fontSize: 14 }}>Customer's request</h3>
                    {estimate.requestNotes && <p style={{ margin: '0 0 12px', fontSize: 13, color: C.text, whiteSpace: 'pre-wrap' }}>{estimate.requestNotes}</p>}
                    {estimate.requestPhotoUrls.length > 0 && (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: 8 }}>
                            {estimate.requestPhotoUrls.map((url, i) => (
                                <a key={i} href={url} target="_blank" rel="noreferrer">
                                    <img src={url} alt={`Request photo ${i + 1}`} style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: 8, border: `1px solid ${C.border}` }} />
                                </a>
                            ))}
                        </div>
                    )}
                </Card>
            )}

            {estimate.convertedJobId && (
                <div style={{ padding: '10px 14px', background: C.greenBg, borderRadius: 8, marginBottom: 16, fontSize: 13, color: C.green }}>
                    ✓ Converted to a job {estimate.convertedAt ? `on ${new Date(estimate.convertedAt).toLocaleDateString()}` : ''}.
                </div>
            )}

            {error && <div style={{ color: '#DC2626', fontSize: 13, marginBottom: 12 }}>{error}</div>}
            {sentLink && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: C.greenBg, borderRadius: 8, marginBottom: 16, fontSize: 13 }}>
                    <span>Sent! Link:</span>
                    <code style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sentLink}</code>
                    <Btn size="sm" variant="outline" onClick={() => navigator.clipboard.writeText(sentLink)}>Copy</Btn>
                    <button onClick={() => setSentLink(null)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: C.textMuted }}>✕</button>
                </div>
            )}

            <LineItemsCard estimate={estimate} locked={locked} onChanged={setEstimate} />
            <EstimateTotalsCard estimate={estimate} locked={locked} onChanged={setEstimate} />
        </div>
    );
}

export function LineItemsCard({ estimate, locked, onChanged }: {
    estimate: EstimateBuilderDocDto; locked: boolean; onChanged: (e: EstimateBuilderDocDto) => void;
}) {
    const [showAdd, setShowAdd] = useState(false);
    const [lineType, setLineType] = useState<EstimateLineType>('Service');
    const [catalogItems, setCatalogItems] = useState<ServiceCatalogItemDto[]>([]);
    const [inventoryItems, setInventoryItems] = useState<InventoryItemDto[]>([]);
    const [selectedCatalogId, setSelectedCatalogId] = useState('');
    const [selectedInventoryId, setSelectedInventoryId] = useState('');
    const [description, setDescription] = useState('');
    const [unit, setUnit] = useState('ea');
    const [quantity, setQuantity] = useState('1');
    const [unitPrice, setUnitPrice] = useState('');
    const [unitCost, setUnitCost] = useState('');
    const [photoBase64, setPhotoBase64] = useState<string | null>(null);
    const [isOptional, setIsOptional] = useState(false);
    const [tierGroup, setTierGroup] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [selectionError, setSelectionError] = useState('');

    // Loaded once so Service/Labor lines can be picked from the tenant's Service Catalog and
    // Material lines from Inventory, instead of only ever being free-typed.
    useEffect(() => {
        api.getServiceCatalog().then(setCatalogItems).catch(() => {});
        api.getInventory().then(setInventoryItems).catch(() => {});
    }, []);

    const reload = async () => onChanged(await api.getEstimateBuilderDoc(estimate.id));

    const handlePhoto = (file: File) => {
        const reader = new FileReader();
        reader.onload = e => setPhotoBase64(e.target?.result as string);
        reader.readAsDataURL(file);
    };

    const changeLineType = (v: string) => {
        setLineType(v as EstimateLineType);
        setSelectedCatalogId(''); setSelectedInventoryId('');
    };

    const pickCatalogItem = (id: string) => {
        setSelectedCatalogId(id);
        setSelectedInventoryId('');
        const item = catalogItems.find(c => c.id === id);
        if (item) { setDescription(item.name); setUnit(item.unit || 'ea'); setUnitPrice(String(item.price)); }
    };

    const pickInventoryItem = (id: string) => {
        setSelectedInventoryId(id);
        setSelectedCatalogId('');
        const item = inventoryItems.find(i => i.id === id);
        if (item) { setDescription(item.name); setUnitCost(String(item.unitCost)); }
    };

    const addItem = async () => {
        if (!description.trim()) { setError('Description is required.'); return; }
        const qty = parseFloat(quantity);
        const price = parseFloat(unitPrice);
        if (isNaN(qty) || qty <= 0) { setError('Quantity must be greater than 0.'); return; }
        if (isNaN(price) || price < 0) { setError('Unit price is required.'); return; }
        setSaving(true); setError('');
        try {
            await api.addEstimateLineItem(estimate.id, {
                catalogItemId: selectedCatalogId || undefined,
                inventoryItemId: selectedInventoryId || undefined,
                lineType, description: description.trim(), unit: unit.trim() || 'ea',
                quantity: qty, unitPrice: price,
                unitCost: unitCost.trim() ? parseFloat(unitCost) : null,
                photoBase64: photoBase64 || undefined,
                isOptional,
                tierGroup: isOptional && tierGroup.trim() ? tierGroup.trim() : null,
            });
            setDescription(''); setUnit('ea'); setQuantity('1'); setUnitPrice(''); setUnitCost(''); setPhotoBase64(null);
            setIsOptional(false); setTierGroup('');
            setSelectedCatalogId(''); setSelectedInventoryId('');
            setShowAdd(false);
            await reload();
        } catch (e: any) { setError(e.message); }
        finally { setSaving(false); }
    };

    const removeItem = async (lineItemId: string) => {
        try { await api.removeEstimateLineItem(estimate.id, lineItemId); await reload(); }
        catch (e: any) { setError(e.message); }
    };

    const toggleSelected = async (li: EstimateLineItemDto) => {
        setSelectionError('');
        try { await api.setEstimateLineItemSelected(estimate.id, li.id, !li.isSelected); await reload(); }
        catch (e: any) { setSelectionError(e.message); }
    };

    return (
        <Card style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h3 style={{ margin: 0, fontSize: 15 }}>Line items</h3>
                {!locked && <Btn size="sm" variant="outline" onClick={() => setShowAdd(s => !s)}>{showAdd ? 'Cancel' : '+ Add line'}</Btn>}
            </div>
            {error && <div style={{ color: '#DC2626', fontSize: 13, marginBottom: 12 }}>{error}</div>}

            {showAdd && (
                <div style={{ padding: 12, background: C.bg, borderRadius: 8, marginBottom: 12, border: `1px solid ${C.border}` }}>
                    <div style={{ display: 'flex', gap: 10 }}>
                        <div style={{ width: 130 }}>
                            <Select label="Type" value={lineType} onChange={changeLineType} options={LINE_TYPE_OPTIONS} />
                        </div>
                        {lineType === 'Material' ? (
                            <div style={{ flex: 1 }}>
                                <Select label="Inventory item" value={selectedInventoryId} onChange={pickInventoryItem}
                                    options={[
                                        { value: '', label: '— Custom item (not in inventory) —' },
                                        ...inventoryItems.map(i => ({ value: i.id, label: `${i.name} (${i.quantityOnHand} on hand)` })),
                                    ]} />
                            </div>
                        ) : (
                            <div style={{ flex: 1 }}>
                                <Select label="Catalog item" value={selectedCatalogId} onChange={pickCatalogItem}
                                    options={[
                                        { value: '', label: '— Custom item (not in catalog) —' },
                                        ...catalogItems.map(c => ({ value: c.id, label: `${c.name} — $${c.price}${c.unit ? '/' + c.unit : ''}` })),
                                    ]} />
                            </div>
                        )}
                    </div>
                    <Input label="Description" value={description} onChange={setDescription} placeholder="e.g. Furnace filter replacement" />
                    <div style={{ display: 'flex', gap: 10 }}>
                        <div style={{ width: 80 }}><Input label="Qty" value={quantity} onChange={setQuantity} type="number" /></div>
                        <div style={{ width: 90 }}><Input label="Unit" value={unit} onChange={setUnit} placeholder="ea / hr" /></div>
                        <div style={{ width: 110 }}><Input label="Price ($)" value={unitPrice} onChange={setUnitPrice} type="number" /></div>
                        <div style={{ width: 110 }}><Input label="Cost ($)" value={unitCost} onChange={setUnitCost} type="number" /></div>
                    </div>
                    <div style={{ fontSize: 11, color: C.textMuted, marginTop: -8, marginBottom: 12 }}>
                        Cost is internal only — never shown to the customer.
                    </div>
                    <div style={{ marginBottom: 12 }}>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: C.textSub, marginBottom: 5 }}>Photo (optional)</label>
                        <input type="file" accept="image/*" onChange={e => { const f = e.target.files?.[0]; if (f) handlePhoto(f); }} />
                        {photoBase64 && <img src={photoBase64} alt="" style={{ display: 'block', marginTop: 6, maxWidth: 100, maxHeight: 100, borderRadius: 6 }} />}
                    </div>
                    <div style={{ marginBottom: 12 }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                            <input type="checkbox" checked={isOptional} onChange={e => setIsOptional(e.target.checked)} />
                            This line is optional / customer-selectable
                        </label>
                        {isOptional && (
                            <div style={{ marginTop: 8, width: 220 }}>
                                <Input label="Tier group (optional)" value={tierGroup} onChange={setTierGroup}
                                    placeholder="e.g. Filter grade — leave blank for a standalone add-on" />
                                <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>
                                    Lines sharing the same tier group name are mutually exclusive — the customer picks
                                    one. Leave blank for a standalone add-on the customer can toggle independently.
                                </div>
                            </div>
                        )}
                    </div>
                    <Btn size="sm" onClick={addItem} disabled={saving}>{saving ? 'Adding…' : 'Add line item'}</Btn>
                </div>
            )}

            {selectionError && <div style={{ color: '#DC2626', fontSize: 13, marginBottom: 8 }}>{selectionError}</div>}

            {estimate.lineItems.length === 0 ? (
                <div style={{ fontSize: 13, color: C.textMuted }}>No line items yet.</div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {estimate.lineItems.map((li: EstimateLineItemDto) => (
                        <div key={li.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: C.bg, borderRadius: 8, opacity: li.isOptional && !li.isSelected ? 0.6 : 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
                                {li.isOptional && (
                                    <input type="checkbox" checked={li.isSelected} disabled={locked}
                                        onChange={() => toggleSelected(li)} title="Included on this estimate" />
                                )}
                                {li.photoUrl && <img src={li.photoUrl} alt="" style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 6 }} />}
                                <div>
                                    <span style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', marginRight: 8 }}>{li.lineType}</span>
                                    <strong>{li.description}</strong>
                                    {li.isOptional && (
                                        <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, color: '#7C3AED' }}>
                                            {li.tierGroup ? `Tier: ${li.tierGroup}` : 'Optional'}
                                        </span>
                                    )}
                                    <div style={{ color: C.textSub, fontSize: 12 }}>
                                        {li.quantity} {li.unit} × ${li.unitPrice.toFixed(2)}
                                        {li.unitCost != null && <span style={{ marginLeft: 8, color: C.textMuted }}>(cost ${li.unitCost.toFixed(2)}/unit)</span>}
                                    </div>
                                </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <strong>{li.isOptional && !li.isSelected ? '—' : `$${li.lineTotal.toFixed(2)}`}</strong>
                                {!locked && <button onClick={() => removeItem(li.id)} style={{ background: 'transparent', border: 'none', color: '#DC2626', cursor: 'pointer', fontSize: 12 }}>Remove</button>}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </Card>
    );
}

export function EstimateTotalsCard({ estimate, locked, onChanged }: {
    estimate: EstimateBuilderDocDto; locked: boolean; onChanged: (e: EstimateBuilderDocDto) => void;
}) {
    const [discountAmount, setDiscountAmount] = useState(String(estimate.discountAmount || ''));
    const [discountLabel, setDiscountLabel] = useState(estimate.discountLabel ?? '');
    const [taxAmount, setTaxAmount] = useState(String(estimate.taxAmount || ''));
    const [depositAmount, setDepositAmount] = useState(estimate.depositAmount != null ? String(estimate.depositAmount) : '');
    const [paymentTerms, setPaymentTerms] = useState(estimate.paymentTerms ?? '');
    const [warrantyNotes, setWarrantyNotes] = useState(estimate.warrantyNotes ?? '');
    const [contractText, setContractText] = useState(estimate.contractDisclaimerText ?? '');
    const [expirationDate, setExpirationDate] = useState(estimate.expirationDate ?? '');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const save = async () => {
        setSaving(true); setError(''); setSuccess('');
        try {
            const updated = await api.updateEstimateBuilderDoc(estimate.id, {
                paymentTerms: paymentTerms.trim() || null,
                warrantyNotes: warrantyNotes.trim() || null,
                contractDisclaimerText: contractText.trim() || null,
                expirationDate: expirationDate || null,
                depositAmount: depositAmount.trim() ? parseFloat(depositAmount) : null,
                discountAmount: parseFloat(discountAmount || '0') || 0,
                discountLabel: discountLabel.trim() || null,
                taxAmount: parseFloat(taxAmount || '0') || 0,
            });
            onChanged(updated);
            setSuccess('Saved.');
        } catch (e: any) { setError(e.message); }
        finally { setSaving(false); }
    };

    return (
        <Card>
            <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>Totals &amp; terms</h3>
            {error && <div style={{ color: '#DC2626', fontSize: 13, marginBottom: 12 }}>{error}</div>}
            {success && <div style={{ color: C.green, fontSize: 13, marginBottom: 12 }}>{success}</div>}

            <div style={{ fontSize: 13, color: C.textSub, marginBottom: 12 }}>Subtotal: <strong style={{ color: C.text }}>${estimate.subtotal.toFixed(2)}</strong></div>

            <div style={{ display: 'flex', gap: 10 }}>
                <div style={{ flex: 1 }}><Input label="Discount amount ($)" value={discountAmount} onChange={setDiscountAmount} type="number" /></div>
                <div style={{ flex: 1 }}><Input label="Discount label" value={discountLabel} onChange={setDiscountLabel} placeholder="e.g. 10% off" /></div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
                <div style={{ flex: 1 }}><Input label="Tax amount ($)" value={taxAmount} onChange={setTaxAmount} type="number" /></div>
                <div style={{ flex: 1 }}><Input label="Deposit required ($, optional)" value={depositAmount} onChange={setDepositAmount} type="number" /></div>
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, margin: '8px 0 16px' }}>Total: ${estimate.totalAmount.toFixed(2)}</div>

            <Input label="Payment terms" value={paymentTerms} onChange={setPaymentTerms} placeholder="e.g. 50% due at approval, balance on completion" />
            <Input label="Expiration date" value={expirationDate} onChange={setExpirationDate} type="date" />

            <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: C.textSub, marginBottom: 5 }}>Warranty / timeline notes</label>
                <textarea value={warrantyNotes} onChange={e => setWarrantyNotes(e.target.value)} rows={3}
                    style={{ width: '100%', padding: '8px 12px', border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, color: C.text, boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical' }} />
            </div>
            <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: C.textSub, marginBottom: 5 }}>Contract / disclaimer text</label>
                <textarea value={contractText} onChange={e => setContractText(e.target.value)} rows={4}
                    style={{ width: '100%', padding: '8px 12px', border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, color: C.text, boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical' }} />
            </div>

            {!locked && <Btn onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save totals & terms'}</Btn>}
        </Card>
    );
}
